// src/pages/ProfilePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  CreditCard,
  Shield,
  Trash2,
  Edit,
  Save,
  X,
  Settings,
  Crown,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { createCustomerPortalSession, fixSubscription, SUBSCRIPTION_PLANS } from '../services/stripe';
import { getUserBibliographyEntries } from '../services/bibliography';
import { resetPassword } from '../services/auth';
import { updateProfile, deleteUser } from 'firebase/auth';
import { doc, updateDoc, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import toast from 'react-hot-toast';
import { FadeIn, StaggerChildren, StaggerItem } from '../components/motion';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { currentUser, userDocument, refreshUserDocument } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [sendingReset, setSendingReset] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    displayName: currentUser?.displayName || '',
    researchFocus: userDocument?.preferences?.researchFocus || '',
    notificationsEnabled: userDocument?.preferences?.notificationsEnabled ?? true
  });

  // Fetch user entries for stats
  useEffect(() => {
    const fetchEntries = async () => {
      if (!currentUser) return;

      try {
        const response = await getUserBibliographyEntries(currentUser.uid, 100); // Get up to 100 for accurate stats
        if (response?.success && response?.entries) {
          setEntries(response.entries);
        } else {
          setEntries([]);
        }
      } catch (error) {
        console.error('Error fetching entries:', error);
        setEntries([]);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchEntries();
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveProfile = async () => {
    setIsLoading(true);
    try {
      // Update Firebase Auth profile
      await updateProfile(auth.currentUser, {
        displayName: formData.displayName
      });

      // Update Firestore user document
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        displayName: formData.displayName,
        'preferences.researchFocus': formData.researchFocus,
        'preferences.notificationsEnabled': formData.notificationsEnabled,
        updatedAt: new Date()
      });

      setIsEditing(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setIsLoading(true);
    try {
      await createCustomerPortalSession(currentUser.uid);
    } catch (error) {
      console.error('Error opening billing portal:', error);
      toast.error('Failed to open billing portal');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFixSubscription = async () => {
    setIsLoading(true);
    try {
      const result = await fixSubscription(currentUser.uid);

      if (result.status === 'fixed') {
        toast.success('Subscription fixed! Refreshing...');
        await refreshUserDocument();
        window.location.reload(); // Force page reload to show updated data
      } else if (result.status === 'already_fixed') {
        toast.success('Subscription already has unlimited entries!');
      } else {
        toast.info(result.message);
      }
    } catch (error) {
      console.error('Error fixing subscription:', error);
      toast.error('Failed to fix subscription');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      displayName: currentUser?.displayName || '',
      researchFocus: userDocument?.preferences?.researchFocus || '',
      notificationsEnabled: userDocument?.preferences?.notificationsEnabled ?? true
    });
    setIsEditing(false);
  };

  const handlePasswordReset = async () => {
    if (!currentUser?.email) {
      toast.error('No email address on file');
      return;
    }
    if (!window.confirm(`Send password reset email to ${currentUser.email}?`)) return;
    setSendingReset(true);
    try {
      await resetPassword(currentUser.email);
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setSendingReset(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    setDeleting(true);
    try {
      // 1. Delete user's bibliography entries
      const entriesQuery = query(
        collection(db, 'bibliography_entries'),
        where('userId', '==', currentUser.uid)
      );
      const entriesSnapshot = await getDocs(entriesQuery);
      await Promise.all(entriesSnapshot.docs.map(d => deleteDoc(d.ref)));

      // 2. Delete user's content generation jobs
      const jobsQuery = query(
        collection(db, 'content_generation_jobs'),
        where('userId', '==', currentUser.uid)
      );
      const jobsSnapshot = await getDocs(jobsQuery);
      await Promise.all(jobsSnapshot.docs.map(d => deleteDoc(d.ref)));

      // 3. Delete user document
      await deleteDoc(doc(db, 'users', currentUser.uid));

      // 4. Delete Firebase Auth user (may require recent login)
      await deleteUser(auth.currentUser);

      toast.success('Account deleted successfully');
      navigate('/');
    } catch (error) {
      console.error('Account deletion error:', error);
      if (error.code === 'auth/requires-recent-login') {
        toast.error('Please sign out and sign back in, then try again. Account deletion requires recent login.');
      } else {
        toast.error('Failed to delete account. Please try again or contact support.');
      }
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date.seconds * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Show loading state while userDocument is loading
  if (!userDocument) {
    return (
      <div className="min-h-screen flex items-center justify-center">
      <SEO title="Profile" noIndex={true} />
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-secondary-700">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 bg-secondary-50/40">
      <SEO title="Profile" noIndex={true} />
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-semibold text-secondary-900 tracking-tight">
            Profile settings
          </h1>
          <p className="mt-1 text-sm text-secondary-500">
            Manage your account, subscription, and preferences.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile Information */}
          <div className="lg:col-span-2">
            <StaggerChildren>
              {/* Basic Information */}
              <StaggerItem>
                <div className="card mb-6">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h2 className="text-base font-semibold text-secondary-900">Basic information</h2>
                      <p className="text-sm text-secondary-500 mt-0.5">Update your name and contact email.</p>
                    </div>

                    <AnimatePresence mode="wait">
                      {!isEditing ? (
                        <motion.button
                          key="edit"
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setIsEditing(true)}
                          className="btn btn-outline"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </motion.button>
                      ) : (
                        <motion.div
                          key="save-cancel"
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          className="flex space-x-2"
                        >
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleCancelEdit}
                            className="btn btn-outline"
                            disabled={isLoading}
                          >
                            <X className="w-4 h-4 mr-2" />
                            Cancel
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleSaveProfile}
                            className="btn btn-primary"
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                            ) : (
                              <Save className="w-4 h-4 mr-2" />
                            )}
                            Save
                          </motion.button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="form-label">Full Name</label>
                      {isEditing ? (
                        <input
                          type="text"
                          name="displayName"
                          className="form-input"
                          value={formData.displayName}
                          onChange={handleInputChange}
                        />
                      ) : (
                        <p className="text-secondary-900 py-2">
                          {currentUser?.displayName || 'Not set'}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="form-label">Email Address</label>
                      <p className="text-secondary-900 py-2">
                        {currentUser?.email}
                      </p>
                      <p className="text-sm text-secondary-600">
                        Email cannot be changed. Contact support if needed.
                      </p>
                    </div>

                    <div>
                      <label className="form-label">Writing Focus</label>
                      {isEditing ? (
                        <input
                          type="text"
                          name="researchFocus"
                          className="form-input"
                          placeholder="e.g., Technology Trends, Personal Development, Creative Nonfiction"
                          value={formData.researchFocus}
                          onChange={handleInputChange}
                        />
                      ) : (
                        <p className="text-secondary-900 py-2">
                          {userDocument?.preferences?.researchFocus || 'Not set'}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="form-label">Account Created</label>
                      <p className="text-secondary-900 py-2">
                        {formatDate(userDocument?.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
              </StaggerItem>

              {/* Preferences */}
              <StaggerItem>
                <div className="card mb-6">
                  <div className="mb-5">
                    <h2 className="text-base font-semibold text-secondary-900">Preferences</h2>
                    <p className="text-sm text-secondary-500 mt-0.5">Notifications and display options.</p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-secondary-900">Email Notifications</h3>
                        <p className="text-sm text-secondary-600">
                          Receive updates about your source summary entries and account
                        </p>
                      </div>
                      {isEditing ? (
                        <input
                          type="checkbox"
                          name="notificationsEnabled"
                          className="w-4 h-4 text-primary border-secondary-300/30 rounded focus:ring-primary"
                          checked={formData.notificationsEnabled}
                          onChange={handleInputChange}
                        />
                      ) : (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          userDocument?.preferences?.notificationsEnabled
                            ? 'bg-green-100 text-green-800'
                            : 'bg-secondary-100 text-secondary-700'
                        }`}>
                          {userDocument?.preferences?.notificationsEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </StaggerItem>

              {/* Danger Zone */}
              <StaggerItem>
                <div className="card card-floating border-red-200 bg-red-50">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                      <Trash2 className="w-5 h-5 text-red-600" />
                    </div>
                    <h2 className="text-xl font-bold text-red-800">
                      Danger Zone
                    </h2>
                  </div>

                  <div className="space-y-4">
                    <p className="text-red-700">
                      Once you delete your account, there is no going back. Please be certain.
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => setShowDeleteModal(true)}
                      className="btn bg-red-600 hover:bg-red-700 text-white"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Account
                    </motion.button>
                  </div>
                </div>
              </StaggerItem>
            </StaggerChildren>
          </div>

          {/* Sidebar */}
          <div>
            <StaggerChildren>
              {/* Subscription Info */}
              <StaggerItem>
                <div className="card mb-6">
                  <h3 className="text-base font-semibold text-secondary-900 mb-4">Subscription</h3>

                  {userDocument?.subscription && (
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-secondary-600">Current Plan</p>
                        <p className="font-semibold text-secondary-900 capitalize">
                          {userDocument.subscription.plan}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm text-secondary-600">Usage this month</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <div className="flex-1 bg-secondary-200/30 rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full transition-all"
                              style={{
                                width: `${Math.min(
                                  (userDocument.subscription.entriesUsed / userDocument.subscription.entriesLimit) * 100,
                                  100
                                )}%`
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium text-secondary-900">
                            {userDocument.subscription.entriesUsed} / {
                              userDocument.subscription.entriesLimit === -1
                                ? '∞'
                                : userDocument.subscription.entriesLimit
                            }
                          </span>
                        </div>
                      </div>

                      <motion.button
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={handleManageBilling}
                        disabled={isLoading}
                        className="btn btn-outline w-full"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Manage Billing
                      </motion.button>

                      {/* Fix Subscription Button - for paid users with old limits */}
                      {(userDocument.subscription.plan === 'student' || userDocument.subscription.plan === 'researcher') &&
                       userDocument.subscription.entriesLimit !== -1 && (
                        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-sm text-yellow-800 mb-2 font-semibold">⚠️ Subscription Limit Issue Detected</p>
                          <p className="text-xs text-yellow-700 mb-3">
                            Your paid plan should have unlimited entries, but it's showing a limit. Click below to fix this.
                          </p>
                          <motion.button
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={handleFixSubscription}
                            disabled={isLoading}
                            className="btn btn-sm bg-yellow-600 hover:bg-yellow-700 text-white w-full"
                          >
                            {isLoading ? 'Fixing...' : 'Fix My Subscription'}
                          </motion.button>
                        </div>
                      )}

                      {/* Manual subscription activation removed — only the Stripe
                          webhook can grant a paid plan. Use Fix My Subscription
                          (which re-syncs from Stripe) if your plan didn't activate. */}
                    </div>
                  )}
                </div>
              </StaggerItem>

              {/* Quick Stats */}
              <StaggerItem>
                <div className="card card-floating mb-6">
                  <h3 className="text-lg font-bold text-secondary-900 mb-4">
                    Quick Stats
                  </h3>

                  {statsLoading ? (
                    <div className="space-y-3 animate-pulse">
                      <div className="flex justify-between">
                        <div className="h-4 bg-secondary-200/20 rounded w-24"></div>
                        <div className="h-4 bg-secondary-200/20 rounded w-8"></div>
                      </div>
                      <div className="flex justify-between">
                        <div className="h-4 bg-secondary-200/20 rounded w-24"></div>
                        <div className="h-4 bg-secondary-200/20 rounded w-8"></div>
                      </div>
                      <div className="flex justify-between">
                        <div className="h-4 bg-secondary-200/20 rounded w-24"></div>
                        <div className="h-4 bg-secondary-200/20 rounded w-16"></div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-secondary-700">Total Entries</span>
                        <span className="font-semibold text-secondary-900">{entries.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-secondary-700">This Month</span>
                        <span className="font-semibold text-secondary-900">
                          {entries.filter(entry => {
                            const entryDate = new Date(entry.createdAt);
                            const now = new Date();
                            return entryDate.getMonth() === now.getMonth() &&
                                   entryDate.getFullYear() === now.getFullYear();
                          }).length}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-secondary-700">Last Entry</span>
                        <span className="font-semibold text-secondary-900">
                          {entries.length > 0
                            ? (() => {
                                const sortedEntries = [...entries].sort((a, b) =>
                                  new Date(b.createdAt) - new Date(a.createdAt)
                                );
                                const lastEntry = sortedEntries[0];
                                const daysDiff = Math.floor(
                                  (new Date() - new Date(lastEntry.createdAt)) / (1000 * 60 * 60 * 24)
                                );
                                if (daysDiff === 0) return 'Today';
                                if (daysDiff === 1) return 'Yesterday';
                                if (daysDiff < 7) return `${daysDiff} days ago`;
                                if (daysDiff < 30) return `${Math.floor(daysDiff / 7)} weeks ago`;
                                return `${Math.floor(daysDiff / 30)} months ago`;
                              })()
                            : 'No entries'
                          }
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </StaggerItem>

              {/* Account Security */}
              <StaggerItem>
                <div className="card">
                  <h3 className="text-base font-semibold text-secondary-900 mb-4">Security</h3>

                  <div className="space-y-3">
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={handlePasswordReset}
                      disabled={sendingReset}
                      className="btn btn-outline w-full text-sm"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      {sendingReset ? 'Sending...' : 'Change Password'}
                    </motion.button>
                  </div>
                </div>
              </StaggerItem>
            </StaggerChildren>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
            <h3 className="text-lg font-bold text-secondary-900 mb-2">Delete Account</h3>
            <p className="text-sm text-secondary-700 mb-4">
              This will permanently delete your account, all your source entries, and all your generated documents. This cannot be undone.
            </p>
            <p className="text-sm text-secondary-700 mb-4">
              Type <strong className="text-red-600">DELETE</strong> to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="w-full px-4 py-2 border border-secondary-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Type DELETE"
              disabled={deleting}
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}
                className="px-4 py-2 text-sm font-medium text-secondary-700 hover:bg-secondary-100 rounded-lg transition-colors"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-red-300 disabled:cursor-not-allowed"
              >
                {deleting ? 'Deleting...' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
